import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, ExternalLink, ArrowRight, Sparkles, Video, MessageSquare } from 'lucide-react';
import { SEO } from '@/components/seo';
import { buttonStyles } from '@/components/ui';

const API_URL = import.meta.env.VITE_API_URL || 'https://lionfish-app-zwr6q.ondigitalocean.app/api/v1';

interface Service {
  id: string;
  slug: string;
  name: string;
  vendor: string;
  url: string;
  description: string;
  category: string;
  hasFreeplan: boolean;
  logoUrl: string | null;
}

export function ServicesHub() {
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_URL}/pages/services`)
      .then(res => res.json())
      .then(data => setServices(data.services || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'image-generator': return <Sparkles className="w-5 h-5" />;
      case 'video-generator': return <Video className="w-5 h-5" />;
      case 'chatbot': return <MessageSquare className="w-5 h-5" />;
      default: return <Sparkles className="w-5 h-5" />;
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'image-generator': return 'bg-pink-100 text-pink-600';
      case 'video-generator': return 'bg-blue-100 text-blue-600';
      case 'chatbot': return 'bg-green-100 text-green-600';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'image-generator': return 'Image Generator';
      case 'video-generator': return 'Video Generator';
      case 'chatbot': return 'AI Chatbot';
      default: return category;
    }
  };

  // Group by category
  const grouped = services.reduce((acc, service) => {
    const cat = service.category || 'other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(service);
    return acc;
  }, {} as Record<string, Service[]>);

  return (
    <>
      <SEO
        title="AI Image Generation Services - Midjourney, DALL-E, Stable Diffusion"
        description="Compare AI image generation services. Learn which watermarks each service uses and how to remove them from your generated images."
        canonical="/services"
      />

      {/* Hero */}
      <section className="section bg-gradient-to-b from-pink-50 via-white to-white">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              AI Image Generation Services
            </h1>
            <p className="text-lg text-gray-600 mb-8">
              Compare popular AI services and learn how to remove watermarks from images
              generated by each platform.
            </p>
            <Link to="/" className={buttonStyles({ size: 'lg' })}>
              Remove Watermarks Free
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Services by Category */}
      <section className="section bg-white">
        <div className="container">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 text-primary-500 animate-spin" />
            </div>
          ) : (
            <div className="space-y-12">
              {Object.entries(grouped).map(([category, categoryServices]) => (
                <div key={category}>
                  <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center gap-2">
                    <span className={`w-8 h-8 rounded-lg flex items-center justify-center ${getCategoryColor(category)}`}>
                      {getCategoryIcon(category)}
                    </span>
                    {getCategoryLabel(category)}
                    <span className="text-sm font-normal text-gray-400">
                      ({categoryServices.length})
                    </span>
                  </h2>

                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {categoryServices.map(service => (
                      <Link
                        key={service.id}
                        to={`/services/${service.slug}`}
                        className="group bg-white border border-gray-200 rounded-xl p-6 hover:border-pink-300 hover:shadow-lg transition-all"
                      >
                        <div className="flex items-start justify-between mb-4">
                          <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center text-2xl">
                            {service.logoUrl ? (
                              <img src={service.logoUrl} alt={service.name} className="w-8 h-8" />
                            ) : (
                              service.name.charAt(0)
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            {service.hasFreeplan && (
                              <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                                Free tier
                              </span>
                            )}
                            <a
                              href={service.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-gray-400 hover:text-gray-600"
                              onClick={e => e.stopPropagation()}
                            >
                              <ExternalLink className="w-4 h-4" />
                            </a>
                          </div>
                        </div>

                        <h3 className="text-lg font-semibold text-gray-900 mb-1 group-hover:text-pink-600 transition-colors">
                          {service.name}
                        </h3>
                        <p className="text-sm text-gray-500 mb-3">by {service.vendor}</p>
                        <p className="text-gray-600 text-sm line-clamp-2">
                          {service.description}
                        </p>
                      </Link>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="section bg-primary-600">
        <div className="container text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Works With All AI Services
          </h2>
          <p className="text-primary-100 mb-8 max-w-xl mx-auto">
            Remove watermarks from images generated by any AI service instantly.
          </p>
          <Link
            to="/"
            className={buttonStyles({
              variant: 'secondary',
              size: 'lg',
              className: 'bg-white text-primary-600 hover:bg-gray-100',
            })}
          >
            Try Free Now
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </>
  );
}

export default ServicesHub;
